// BottomNav.js
// Responsive navigation: side nav on desktop, bottom nav on mobile

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, getDoc, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useAdmin } from '../contexts/AdminContext';
import AddTodoForm from './AddTodoForm';

function BottomNav({ user, userProfile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userFamilyId, setUserFamilyId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [familyData, setFamilyData] = useState(null);
  const [isPremium, setIsPremium] = useState(false);

  // Admin mode - use viewingFamilyId if set
  const { isInAdminView, viewingFamilyId, viewingFamilyData } = useAdmin();
  const effectiveFamilyId = isInAdminView ? viewingFamilyId : userFamilyId;

  // Get current user
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUserId(user?.uid || null);
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Get user's familyId from Firestore
  useEffect(() => {
    if (!currentUserId) return;

    const unsubscribe = onSnapshot(doc(db, 'users', currentUserId), (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        setUserFamilyId(userData.familyId || null);
      }
    });
    return () => unsubscribe();
  }, [currentUserId]);

  // Fetch family data and premium status
  useEffect(() => {
    const fetchFamilyData = async () => {
      if (isInAdminView && viewingFamilyData) {
        setFamilyData(viewingFamilyData);
        // Check premium status
        if (viewingFamilyData.licenseCode) {
          const purchasesQuery = query(
            collection(db, 'purchases'),
            where('licenseCode', '==', viewingFamilyData.licenseCode),
            where('status', '==', 'active')
          );
          const snapshot = await getDocs(purchasesQuery);
          setIsPremium(!snapshot.empty);
        }
        return;
      }

      if (effectiveFamilyId) {
        const familyDoc = await getDoc(doc(db, 'families', effectiveFamilyId));
        if (familyDoc.exists()) {
          const data = familyDoc.data();
          setFamilyData(data);
          
          // Check premium status
          if (data.licenseCode) {
            const purchasesQuery = query(
              collection(db, 'purchases'),
              where('licenseCode', '==', data.licenseCode),
              where('status', '==', 'active')
            );
            const snapshot = await getDocs(purchasesQuery);
            setIsPremium(!snapshot.empty);
          }
        }
      }
    };

    fetchFamilyData();
  }, [effectiveFamilyId, isInAdminView, viewingFamilyData]);

  // Listen for unread notifications
  useEffect(() => {
    if (!effectiveFamilyId || !currentUserId) return;

    const q = query(
      collection(db, 'notifications'),
      where('familyId', '==', effectiveFamilyId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      // Count unread notifications not created by current user
      const unread = notifications.filter(
        (n) => n.actorId !== currentUserId && !n.readBy?.includes(currentUserId)
      );
      setUnreadCount(unread.length);
    });

    return () => unsubscribe();
  }, [effectiveFamilyId, currentUserId]);

  const mainTabs = [
    { path: '/todos', label: 'Home', icon: '🏠' },
    { path: '/discover', label: 'Discover', icon: '🔍' },
    { path: '/notifications', label: 'Notifications', icon: '🔔', badge: unreadCount }
  ];

  const settingsTab = { path: '/settings', label: 'Settings', icon: '⚙️' };
  
  // For mobile, combine all tabs
  const allTabs = [...mainTabs, settingsTab];

  const handleCreateClick = () => {
    setShowAddForm(true);
  };

  // Use user prop if provided, otherwise fall back to currentUser from auth
  const effectiveUser = user || (currentUser ? { uid: currentUser.uid, email: currentUser.email || '' } : null);

  return (
    <>
      <nav className="bottom-nav">
        {/* Mobile: horizontal layout with create button in middle */}
        <div className="bottom-nav-mobile">
          {allTabs.slice(0, 2).map((tab) => (
            <button
              key={tab.path}
              className={`bottom-nav-item ${location.pathname === tab.path ? 'active' : ''}`}
              onClick={() => navigate(tab.path)}
            >
              <span className="bottom-nav-icon">
                {tab.icon}
                {tab.badge > 0 && (
                  <span className="bottom-nav-badge">{tab.badge > 99 ? '99+' : tab.badge}</span>
                )}
              </span>
              <span className="bottom-nav-label">{tab.label}</span>
            </button>
          ))}
          
          {/* Create button in the middle */}
          <button
            className="bottom-nav-create-button"
            onClick={handleCreateClick}
            aria-label="Create new task"
          >
            <span className="bottom-nav-create-icon">+</span>
          </button>

          {allTabs.slice(2).map((tab) => (
            <button
              key={tab.path}
              className={`bottom-nav-item ${location.pathname === tab.path ? 'active' : ''}`}
              onClick={() => navigate(tab.path)}
            >
              <span className="bottom-nav-icon">
                {tab.icon}
                {tab.badge > 0 && (
                  <span className="bottom-nav-badge">{tab.badge > 99 ? '99+' : tab.badge}</span>
                )}
              </span>
              <span className="bottom-nav-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Desktop: vertical side nav */}
        <div className="side-nav-desktop">
          {/* Logo at top */}
          <button 
            className="side-nav-logo"
            onClick={() => navigate('/todos')}
            aria-label="Go to Home"
          >
            <img src="/assets/black_logo.png" alt="Kana" className="side-nav-logo-img" />
            <span className="side-nav-logo-text">Kana</span>
          </button>

          {/* Create button at top */}
          <button
            className="side-nav-create-button"
            onClick={handleCreateClick}
            aria-label="Create new task"
          >
            <span className="side-nav-create-icon">+</span>
            <span className="side-nav-create-label">Create Task</span>
          </button>

          {/* Main navigation items */}
          <div className="side-nav-main">
            {mainTabs.map((tab) => (
              <button
                key={tab.path}
                className={`side-nav-item ${location.pathname === tab.path ? 'active' : ''}`}
                onClick={() => navigate(tab.path)}
              >
                <span className="side-nav-icon">
                  {tab.icon}
                  {tab.badge > 0 && (
                    <span className="side-nav-badge">{tab.badge > 99 ? '99+' : tab.badge}</span>
                  )}
                </span>
                <span className="side-nav-label">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Settings at bottom */}
          <div className="side-nav-footer">
            <button
              className={`side-nav-item ${location.pathname === settingsTab.path ? 'active' : ''}`}
              onClick={() => navigate(settingsTab.path)}
            >
              <span className="side-nav-icon">{settingsTab.icon}</span>
              <span className="side-nav-label">{settingsTab.label}</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Add todo form */}
      {showAddForm && effectiveUser && effectiveFamilyId && familyData && (
        <AddTodoForm
          onClose={() => setShowAddForm(false)}
          familyId={effectiveFamilyId}
          familyData={familyData}
          currentUserId={effectiveUser.uid}
          userEmail={effectiveUser.email || ''}
          isPremium={isPremium}
        />
      )}
    </>
  );
}

export default BottomNav;
